import { create } from 'zustand';
import { User } from '../types';
import { supabase } from '../lib/supabase';
import { useNylasAuthStore } from './nylasAuth';

let sessionCheckCache: { isValid: boolean; timestamp: number } | null = null;
const SESSION_CACHE_DURATION = 2 * 60 * 1000;
const AUTH_TIMEOUT = 10000; // 10 seconds timeout for all auth operations

const AUTH_STORAGE_KEY = 'crm-auth';

// Timeout wrapper for auth operations
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number = AUTH_TIMEOUT): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Auth operation timed out')), timeoutMs)
    )
  ]);
}

function clearAuthStorage() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
  sessionCheckCache = null;
}

export async function checkSessionValidity(): Promise<boolean> {
  if (sessionCheckCache && Date.now() - sessionCheckCache.timestamp < SESSION_CACHE_DURATION) {
    return sessionCheckCache.isValid;
  }

  try {
    const { data: { session }, error } = await withTimeout(supabase.auth.getSession());

    if (error?.message?.includes('refresh_token_not_found') ||
        error?.message?.includes('session_not_found') ||
        error?.message?.includes('JWT')) {
      sessionCheckCache = { isValid: false, timestamp: Date.now() };
      return false;
    }

    const isValid = !error && !!session;
    sessionCheckCache = { isValid, timestamp: Date.now() };

    return isValid;
  } catch (error: any) {
    console.error('Session validity check failed:', error);
    sessionCheckCache = { isValid: false, timestamp: Date.now() };
    return false;
  }
}

export async function refreshSession(): Promise<boolean> {
  try {
    // First check if we have a session to refresh
    const { data: { session: currentSession } } = await withTimeout(supabase.auth.getSession());

    // If no session or no refresh token, cannot refresh
    if (!currentSession) {
      console.warn('Cannot refresh: no active session');
      sessionCheckCache = { isValid: false, timestamp: Date.now() };
      return false;
    }

    const { data: { session }, error } = await withTimeout(supabase.auth.refreshSession());

    if (error || !session) {
      console.error('Session refresh failed:', error);
      sessionCheckCache = { isValid: false, timestamp: Date.now() };
      return false;
    }

    sessionCheckCache = { isValid: true, timestamp: Date.now() };
    return true;
  } catch (error: any) {
    console.error('Error refreshing session:', error);
    sessionCheckCache = { isValid: false, timestamp: Date.now() };
    return false;
  }
}

export function isAuthError(error: any): boolean {
  return error.message?.includes('JWT') ||
         error.message?.includes('AuthApiError') ||
         error.message?.includes('refresh_token_not_found') ||
         error.message?.includes('session_not_found') ||
         (error.status && (error.status === 401 || error.status === 403));
}

interface AuthState {
  user: User | null;
  loading: boolean;
  sessionValid: boolean;
  taskRefreshCounter: number;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
  checkSession: () => Promise<boolean>;
  refreshSession: () => Promise<boolean>;
  handleAuthError: () => Promise<void>;
  triggerTaskRefresh: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  sessionValid: true,
  taskRefreshCounter: 0,
  signIn: async (email: string, password: string) => {
    const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) throw signInError;

    if (!authData.user) {
      throw new Error('No user data returned after sign in');
    }

    const { data: userSettings } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', authData.user.id)
      .single();

    set({
      user: {
        id: authData.user.id,
        email: authData.user.email!,
        role: (authData.user.user_metadata?.role as 'admin' | 'user') || 'user',
        created_at: authData.user.created_at,
        user_settings: userSettings || undefined
      },
      sessionValid: true
    });
  },
  signOut: async () => {
    try {
      clearAuthStorage();
      useNylasAuthStore.getState().reset();
      await supabase.auth.signOut();
      set({ user: null, sessionValid: false });
    } catch (error) {
      console.error('Error during sign out:', error);
      useNylasAuthStore.getState().reset();
      set({ user: null, sessionValid: false });
    }
  },
  handleAuthError: async () => {
    try {
      clearAuthStorage();
      useNylasAuthStore.getState().reset();
      await supabase.auth.signOut();
      set({ user: null, sessionValid: false, loading: false });
    } catch (error) {
      console.error('Error handling auth error:', error);
      useNylasAuthStore.getState().reset();
      set({ user: null, sessionValid: false, loading: false });
    }
  },
  initialize: async () => {
    try {
      const { data: { session }, error: sessionError } = await withTimeout(supabase.auth.getSession());

      if (sessionError) {
        if (sessionError.message.includes('refresh_token_not_found') ||
            sessionError.message.includes('session_not_found')) {
          clearAuthStorage();
          set({ user: null, loading: false, sessionValid: false });
          return;
        }
        console.error('Error getting session:', sessionError);
        set({ loading: false, sessionValid: false });
        return;
      }

      if (session?.user) {
        const { data: userSettings } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', session.user.id)
          .single();

        set({
          user: {
            id: session.user.id,
            email: session.user.email!,
            role: (session.user.user_metadata?.role as 'admin' | 'user') || 'user',
            created_at: session.user.created_at,
            user_settings: userSettings || undefined
          },
          sessionValid: true
        });
      } else {
        set({ sessionValid: false });
      }

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          // CRITICAL: No async operations here to prevent deadlock
          // Fetch user settings separately after state update
          if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
            clearAuthStorage();
            set({ user: null, sessionValid: false });
          } else if (event === 'SIGNED_IN' && session?.user) {
            // Set user immediately without waiting for settings
            set({
              user: {
                id: session.user.id,
                email: session.user.email!,
                role: (session.user.user_metadata?.role as 'admin' | 'user') || 'user',
                created_at: session.user.created_at,
                user_settings: undefined
              },
              sessionValid: true
            });

            // Fetch user settings asynchronously without blocking
            (async () => {
              try {
                const { data: userSettings } = await supabase
                  .from('user_settings')
                  .select('*')
                  .eq('user_id', session.user.id)
                  .single();

                if (userSettings) {
                  set((state) => ({
                    user: state.user ? { ...state.user, user_settings: userSettings } : null
                  }));
                }
              } catch (error) {
                console.error('Error fetching user settings:', error);
              }
            })();
          } else if (event === 'TOKEN_REFRESHED' && !session) {
            console.error('Token refresh failed - session is null');
            clearAuthStorage();
            set({ user: null, sessionValid: false });
          } else if (event === 'TOKEN_REFRESHED' && session) {
            set({ sessionValid: true });
            sessionCheckCache = { isValid: true, timestamp: Date.now() };
          }
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    } catch (error: any) {
      console.error('Error initializing auth:', error);
      if (error.message?.includes('refresh_token_not_found') ||
          error.message?.includes('session_not_found')) {
        clearAuthStorage();
        set({ user: null, sessionValid: false });
      }
    } finally {
      set({ loading: false });
    }
  },
  checkSession: async () => {
    const isValid = await checkSessionValidity();

    if (!isValid) {
      clearAuthStorage();
      set({ sessionValid: false });
    } else {
      set({ sessionValid: true });
    }

    return isValid;
  },
  refreshSession: async () => {
    const success = await refreshSession();

    if (!success) {
      clearAuthStorage();
      set({ sessionValid: false });
    } else {
      set({ sessionValid: true });
    }

    return success;
  },
  triggerTaskRefresh: () => {
    set((state) => ({ taskRefreshCounter: state.taskRefreshCounter + 1 }));
  }
}));
